/*
🐝 BeeNetwork Hive Management System
GetMyHive Operations Center - Dijital Mesh Ağ Yönetimi
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Input,
  Select,
  Tag,
  Avatar,
  Badge,
  List,
  Timeline,
  Modal,
  Form,
  Tabs,
  Progress,
  Alert,
  Divider,
  Tooltip,
  Switch,
  Slider,
  Descriptions,
  Collapse,
  Steps
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  RobotOutlined,
  BarChartOutlined,
  SettingOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  BellOutlined,
  GlobalOutlined,
  AimOutlined,
  RadarChartOutlined
} from '@ant-design/icons';
import GoogleMap, { AlarmMarker } from '../components/GoogleMap';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// Mock Hive Data - AI tarafından otomatik oluşturulan mahalle bazlı hive'ler
const mockHives = [
  {
    id: 'H001',
    name: 'Kadıköy-Merkez-H1',
    district: 'Kadıköy',
    neighborhood: 'Merkez Mahallesi',
    coordinates: { lat: 40.9827, lng: 29.0375 },
    status: 'active',
    aiGenerated: true,
    createdAt: '2024-01-10 09:30',
    members: {
      total: 245,
      asil: 89,
      yeni: 134,
      misafir: 22,
      active: 89,
      verified: 201
    },
    coverage: {
      radius: 1.2, // km
      population: 3450,
      buildings: 156,
      businesses: 78
    },
    performance: {
      responseTime: 4.2, // minutes
      emergencyHandled: 23,
      communityScore: 94,
      safetyIndex: 87
    },
    aiMetrics: {
      optimalSize: true,
      densityScore: 92,
      connectivityIndex: 88,
      efficiencyRating: 91
    }
  },
  {
    id: 'H002',
    name: 'Şişli-Kuzey-H3',
    district: 'Şişli',
    neighborhood: 'Kuzey Mahallesi',
    coordinates: { lat: 41.0686, lng: 28.9944 },
    status: 'active',
    aiGenerated: true,
    createdAt: '2024-01-12 14:15',
    members: {
      total: 189,
      asil: 67,
      yeni: 98,
      misafir: 24,
      active: 67,
      verified: 156
    },
    coverage: {
      radius: 0.9,
      population: 2890,
      buildings: 134,
      businesses: 89
    },
    performance: {
      responseTime: 3.8,
      emergencyHandled: 18,
      communityScore: 89,
      safetyIndex: 92
    },
    aiMetrics: {
      optimalSize: true,
      densityScore: 87,
      connectivityIndex: 94,
      efficiencyRating: 89
    }
  }
];

// Mock AI Recommendations
const mockAIRecommendations = [
  {
    id: '1',
    type: 'hive_split',
    hive: 'Beşiktaş-Sahil-H2',
    priority: 'high',
    recommendation: 'Bu hive optimal boyutu aştı. 2 ayrı hive\'e bölünmesi önerilir.',
    impact: 'Yanıt süresi %40 iyileşir, topluluk skoru %15 artar',
    confidence: 89
  }
];

const BeeNetworkPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hives');
  const [hives, setHives] = useState(mockHives);
  const [filteredHives, setFilteredHives] = useState(mockHives);
  const [selectedHive, setSelectedHive] = useState<any>(null);
  const [hiveDetailModal, setHiveDetailModal] = useState(false);
  const [createHiveModal, setCreateHiveModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [form] = Form.useForm();

  // Statistics
  const [stats, setStats] = useState({
    totalHives: 5,
    totalMembers: 1033,
    activeHives: 4,
    aiOptimized: 5
  });

  useEffect(() => {
    loadBeeNetworkData();
  }, []);

  useEffect(() => {
    filterHives();
  }, [hives, searchTerm, statusFilter, districtFilter]);

  const loadBeeNetworkData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('BeeNetwork data loaded');
    } catch (error) {
      console.error('BeeNetwork loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterHives = () => {
    let filtered = hives;

    if (searchTerm) {
      filtered = filtered.filter(hive =>
        hive.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hive.district.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hive.neighborhood.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(hive => hive.status === statusFilter);
    }

    if (districtFilter !== 'all') {
      filtered = filtered.filter(hive => hive.district === districtFilter);
    }

    setFilteredHives(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#52c41a';
      case 'expanding': return '#1890ff';
      case 'optimizing': return '#fa8c16';
      case 'inactive': return '#d9d9d9';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'expanding': return 'Genişliyor';
      case 'optimizing': return 'Optimize Ediliyor';
      case 'inactive': return 'Pasif';
      default: return 'Bilinmiyor';
    }
  };

  const getMemberTypeColor = (type: string) => {
    switch (type) {
      case 'asil': return '#52c41a';
      case 'yeni': return '#1890ff';
      case 'misafir': return '#fa8c16';
      default: return '#d9d9d9';
    }
  };

  const openHiveDetail = (hive: any) => {
    setSelectedHive(hive);
    setHiveDetailModal(true);
  };

  const createNewHive = async (values: any) => {
    setLoading(true);
    try {
      console.log('Creating new hive:', values);
      // Simulate AI hive creation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const newHive = {
        id: `H${String(hives.length + 1).padStart(3, '0')}`,
        name: `${values.district}-${values.neighborhood}-H${hives.length + 1}`,
        district: values.district,
        neighborhood: values.neighborhood,
        coordinates: { lat: 41.0000 + Math.random() * 0.1, lng: 29.0000 + Math.random() * 0.1 },
        status: 'optimizing',
        aiGenerated: true,
        createdAt: new Date().toLocaleString('tr-TR'),
        members: {
          total: 0,
          asil: 0,
          yeni: 0,
          misafir: 0,
          active: 0,
          verified: 0
        },
        coverage: {
          radius: values.radius || 1.0,
          population: values.population || 2000,
          buildings: Math.floor((values.population || 2000) / 20),
          businesses: Math.floor((values.population || 2000) / 40)
        },
        performance: {
          responseTime: 0,
          emergencyHandled: 0,
          communityScore: 0,
          safetyIndex: 0
        },
        aiMetrics: {
          optimalSize: true,
          densityScore: 0,
          connectivityIndex: 0,
          efficiencyRating: 0
        }
      };

      setHives(prev => [newHive, ...prev]);
      setCreateHiveModal(false);
      form.resetFields();
    } catch (error) {
      console.error('Hive creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hives Tab
  const renderHives = () => (
    <div>
      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Search
              placeholder="Hive ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Durum"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">Tüm Durumlar</Option>
              <Option value="active">Aktif</Option>
              <Option value="expanding">Genişliyor</Option>
              <Option value="optimizing">Optimize Ediliyor</Option>
              <Option value="inactive">Pasif</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="İlçe"
              value={districtFilter}
              onChange={setDistrictFilter}
            >
              <Option value="all">Tüm İlçeler</Option>
              <Option value="Kadıköy">Kadıköy</Option>
              <Option value="Şişli">Şişli</Option>
              <Option value="Beşiktaş">Beşiktaş</Option>
              <Option value="Üsküdar">Üsküdar</Option>
              <Option value="Bakırköy">Bakırköy</Option>
            </Select>
          </Col>
          <Col xs={24} md={10}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setCreateHiveModal(true)}
              >
                Yeni Hive Oluştur
              </Button>
              <Button 
                icon={<RobotOutlined />}
                type="dashed"
              >
                AI Optimizasyonu
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadBeeNetworkData}
                loading={loading}
              >
                Yenile
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Hives Grid */}
      <Row gutter={[16, 16]}>
        {filteredHives.map((hive) => (
          <Col xs={24} md={12} lg={8} key={hive.id}>
            <Card
              hoverable
              actions={[
                <Button 
                  size="small" 
                  icon={<EyeOutlined />}
                  onClick={() => openHiveDetail(hive)}
                >
                  Detay
                </Button>,
                <Button size="small" icon={<EditOutlined />}>
                  Düzenle
                </Button>,
                <Button size="small" icon={<BarChartOutlined />}>
                  Analiz
                </Button>
              ]}
            >
              <Card.Meta
                avatar={
                  <Badge dot={hive.status === 'active'}>
                    <Avatar 
                      icon={<TeamOutlined />}
                      style={{ backgroundColor: getStatusColor(hive.status) }}
                      size="large"
                    />
                  </Badge>
                }
                title={
                  <Space direction="vertical" size="small">
                    <Text strong>{hive.name}</Text>
                    <Space>
                      <Tag color={getStatusColor(hive.status)}>
                        {getStatusText(hive.status)}
                      </Tag>
                      {hive.aiGenerated && (
                        <Tag color="blue">AI</Tag>
                      )}
                    </Space>
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">
                      <EnvironmentOutlined /> {hive.district} - {hive.neighborhood}
                    </Text>
                    
                    <Divider style={{ margin: '8px 0' }} />
                    
                    <Row gutter={[8, 4]}>
                      <Col span={12}>
                        <Text type="secondary">Toplam Üye:</Text>
                        <br />
                        <Text strong>{hive.members.total}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Aktif:</Text>
                        <br />
                        <Text strong style={{ color: '#52c41a' }}>{hive.members.active}</Text>
                      </Col>
                    </Row>
                    
                    <Row gutter={[8, 4]}>
                      <Col span={8}>
                        <Tag color={getMemberTypeColor('asil')} style={{ margin: 0 }}>
                          Asil: {hive.members.asil}
                        </Tag>
                      </Col>
                      <Col span={8}>
                        <Tag color={getMemberTypeColor('yeni')} style={{ margin: 0 }}>
                          Yeni: {hive.members.yeni}
                        </Tag>
                      </Col>
                      <Col span={8}>
                        <Tag color={getMemberTypeColor('misafir')} style={{ margin: 0 }}>
                          Misafir: {hive.members.misafir}
                        </Tag>
                      </Col>
                    </Row>

                    <Divider style={{ margin: '8px 0' }} />

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Topluluk Skoru:</Text>
                        <Progress 
                          percent={hive.performance.communityScore} 
                          size="small"
                          strokeColor="#52c41a"
                        />
                      </div>
                      <div>
                        <Text type="secondary">AI Verimlilik:</Text>
                        <Progress 
                          percent={hive.aiMetrics.efficiencyRating} 
                          size="small"
                          strokeColor="#1890ff"
                        />
                      </div>
                    </Space>
                  </Space>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  // AI Recommendations Tab with Advanced Hive Monitoring
  const renderAIRecommendations = () => {
    // AI Hive Monitoring Data
    const aiHiveAnalysis = {
      autoDetectedHives: [
        {
          id: 'AI-DETECT-001',
          location: 'Fatih, Eminönü Çevresi',
          coordinates: { lat: 41.0186, lng: 28.9647 },
          detectionTime: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          confidence: 87,
          memberDensity: 'Yüksek (45+ kişi)',
          activityPattern: 'Düzenli toplantılar tespit edildi',
          riskLevel: 'Düşük',
          status: 'Onay Bekliyor',
          aiRecommendation: 'Yeni hive oluşturulması öneriliyor'
        },
        {
          id: 'AI-DETECT-002',
          location: 'Beyoğlu, Galata Kulesi Yakını',
          coordinates: { lat: 41.0256, lng: 28.9744 },
          detectionTime: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
          confidence: 92,
          memberDensity: 'Orta (25-30 kişi)',
          activityPattern: 'Haftalık düzenli aktivite',
          riskLevel: 'Çok Düşük',
          status: 'AI Analizi Tamamlandı',
          aiRecommendation: 'Mevcut hive ile birleştirilebilir'
        },
        {
          id: 'AI-DETECT-003',
          location: 'Sarıyer, Maslak İş Merkezi',
          coordinates: { lat: 41.1086, lng: 29.0119 },
          detectionTime: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
          confidence: 74,
          memberDensity: 'Düşük (12-15 kişi)',
          activityPattern: 'İş saatleri aktivitesi',
          riskLevel: 'Orta',
          status: 'Daha Fazla Veri Gerekli',
          aiRecommendation: 'İzlemeye devam edilmeli'
        }
      ],
      areaAnalysis: {
        totalAreasScanned: 47,
        newAreasDetected: 3,
        highPotentialAreas: 8,
        analysisAccuracy: 89.3,
        lastScanTime: new Date(Date.now() - 1000 * 60 * 10).toISOString()
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Onay Bekliyor': return 'orange';
        case 'AI Analizi Tamamlandı': return 'green';
        case 'Daha Fazla Veri Gerekli': return 'blue';
        default: return 'default';
      }
    };

    const getRiskColor = (risk: string) => {
      switch (risk) {
        case 'Çok Düşük': return 'green';
        case 'Düşük': return 'lime';
        case 'Orta': return 'orange';
        case 'Yüksek': return 'red';
        default: return 'default';
      }
    };

    return (
      <div>
        {/* AI Hive Monitoring Header */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Alert
              message="🤖 AI Hive İzleme & Otomatik Tanımlama Sistemi"
              description="Yapay zeka sürekli olarak şehir genelinde hive potansiyeli olan alanları tarar ve otomatik hive önerileri sunar."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </Col>
        </Row>

        {/* AI Analysis Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={6}>
            <Card>
              <Statistic
                title="Taranan Alan"
                value={aiHiveAnalysis.areaAnalysis.totalAreasScanned}
                prefix={<RadarChartOutlined />}
                suffix="bölge"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic
                title="Yeni Tespit"
                value={aiHiveAnalysis.areaAnalysis.newAreasDetected}
                prefix={<EyeOutlined />}
                suffix="alan"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic
                title="Yüksek Potansiyel"
                value={aiHiveAnalysis.areaAnalysis.highPotentialAreas}
                prefix={<CheckCircleOutlined />}
                suffix="bölge"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic
                title="AI Doğruluğu"
                value={aiHiveAnalysis.areaAnalysis.analysisAccuracy}
                prefix={<RobotOutlined />}
                suffix="%"
                precision={1}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Auto-Detected Hives */}
        <Card 
          title={
            <Space>
              <RobotOutlined style={{ color: '#1890ff' }} />
              <span>🎯 AI Tarafından Tespit Edilen Hive Alanları</span>
            </Space>
          }
          extra={
            <Space>
              <Text type="secondary">
                Son tarama: {new Date(aiHiveAnalysis.areaAnalysis.lastScanTime).toLocaleString('tr-TR')}
              </Text>
              <Button icon={<ReloadOutlined />} size="small">
                Yeniden Tara
              </Button>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <List
            dataSource={aiHiveAnalysis.autoDetectedHives}
            renderItem={(detection) => (
              <List.Item
                actions={[
                  <Button type="primary" size="small" icon={<CheckCircleOutlined />}>
                    Hive Oluştur
                  </Button>,
                  <Button size="small" icon={<EyeOutlined />}>
                    Haritada Göster
                  </Button>,
                  <Button size="small" icon={<BarChartOutlined />}>
                    Detaylı Analiz
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={detection.confidence + '%'} style={{ backgroundColor: '#52c41a' }}>
                      <Avatar 
                        icon={<RobotOutlined />}
                        style={{ backgroundColor: '#1890ff' }}
                        size="large"
                      />
                    </Badge>
                  }
                  title={
                    <Space wrap>
                      <Text strong>{detection.location}</Text>
                      <Tag color={getStatusColor(detection.status)}>
                        {detection.status}
                      </Tag>
                      <Tag color={getRiskColor(detection.riskLevel)}>
                        Risk: {detection.riskLevel}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Row gutter={[16, 8]}>
                        <Col span={12}>
                          <Text type="secondary">
                            <strong>👥 Üye Yoğunluğu:</strong> {detection.memberDensity}
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">
                            <strong>📍 Koordinat:</strong> {detection.coordinates.lat.toFixed(4)}, {detection.coordinates.lng.toFixed(4)}
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">
                            <strong>📊 Aktivite:</strong> {detection.activityPattern}
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">
                            <strong>⏰ Tespit:</strong> {new Date(detection.detectionTime).toLocaleString('tr-TR')}
                          </Text>
                        </Col>
                      </Row>
                      <Alert
                        message={detection.aiRecommendation}
                        type="success"
                        size="small"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        {/* Existing AI Recommendations */}
        <Card 
          title={
            <Space>
              <RobotOutlined style={{ color: '#722ed1' }} />
              <span>💡 Mevcut Hive Optimizasyon Önerileri</span>
            </Space>
          }
        >
          <List
            dataSource={mockAIRecommendations}
            renderItem={(recommendation) => (
              <List.Item
                actions={[
                  <Button type="primary" size="small">Uygula</Button>,
                  <Button size="small">Detay</Button>,
                  <Button size="small" danger>Reddet</Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={<RobotOutlined />}
                      style={{ backgroundColor: '#722ed1' }}
                    />
                  }
                  title={
                    <Space>
                      <Text strong>{recommendation.hive}</Text>
                      <Tag color={recommendation.priority === 'high' ? 'red' : 
                                  recommendation.priority === 'medium' ? 'orange' : 'blue'}>
                        {recommendation.priority.toUpperCase()}
                      </Tag>
                      <Tag color="blue">%{recommendation.confidence} güvenilir</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text>{recommendation.recommendation}</Text>
                      <Text type="secondary">
                        <strong>Beklenen Etki:</strong> {recommendation.impact}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </div>
    );
  };

  // Network Analytics Tab
  const renderNetworkAnalytics = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={16}>
        <Card title="📊 Ağ Performans Analizi">
          <Timeline>
            <Timeline.Item color="green">
              <Text strong>Toplam Hive Sayısı:</Text> 5 adet (hedef: 8-10)
            </Timeline.Item>
            <Timeline.Item color="blue">
              <Text strong>Ortalama Yanıt Süresi:</Text> 4.3 dakika (hedef: &lt;5 dk)
            </Timeline.Item>
            <Timeline.Item color="orange">
              <Text strong>Ağ Kapsama Oranı:</Text> %67 (hedef: %85)
            </Timeline.Item>
            <Timeline.Item color="purple">
              <Text strong>AI Optimizasyon Skoru:</Text> %87 (mükemmel)
            </Timeline.Item>
          </Timeline>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card title="🎯 Ağ Metrikleri">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text type="secondary">Hive Bağlantı Kalitesi</Text>
              <Progress percent={89} strokeColor="#52c41a" />
            </div>
            <div>
              <Text type="secondary">Üye Dağılım Dengesi</Text>
              <Progress percent={76} strokeColor="#1890ff" />
            </div>
            <div>
              <Text type="secondary">Coğrafi Kapsama</Text>
              <Progress percent={67} strokeColor="#fa8c16" />
            </div>
            <div>
              <Text type="secondary">AI Optimizasyon</Text>
              <Progress percent={87} strokeColor="#722ed1" />
            </div>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  // Google Maps Hive Visualization
  const renderHiveMap = () => {
    // Convert hive data to AlarmMarker format for Google Maps
    const hiveLocations = [
      {
        id: 'HIVE-001',
        name: 'Kadıköy Merkez Hive',
        lat: 40.9909,
        lng: 29.0253,
        neighborhood: 'Kadıköy',
        status: 'active',
        members: 45,
        type: 'principal'
      },
      {
        id: 'HIVE-002', 
        name: 'Beşiktaş Dijital Hive',
        lat: 41.0422,
        lng: 29.0094,
        neighborhood: 'Beşiktaş',
        status: 'active',
        members: 38,
        type: 'principal'
      },
      {
        id: 'HIVE-003',
        name: 'Şişli Yeni Hive',
        lat: 41.0608,
        lng: 28.9864,
        neighborhood: 'Şişli',
        status: 'pending',
        members: 12,
        type: 'new'
      },
      {
        id: 'HIVE-004',
        name: 'Üsküdar Güvenlik Hive',
        lat: 41.0214,
        lng: 29.0742,
        neighborhood: 'Üsküdar',
        status: 'active',
        members: 52,
        type: 'principal'
      },
      {
        id: 'HIVE-005',
        name: 'Bakırköy Misafir Hive',
        lat: 40.9744,
        lng: 28.8719,
        neighborhood: 'Bakırköy',
        status: 'maintenance',
        members: 8,
        type: 'guest'
      }
    ];

    // Helper function - MUST be defined before use
    const getHiveSeverity = (type: string, status: string): 'critical' | 'high' | 'medium' | 'low' => {
      if (status === 'maintenance') return 'critical';
      if (status === 'pending') return 'high';
      if (type === 'principal') return 'low';
      if (type === 'new') return 'medium';
      return 'low';
    };

    // Convert to AlarmMarker format for GoogleMap component
    const hiveMarkers: AlarmMarker[] = hiveLocations.map(hive => ({
      id: hive.id,
      lat: hive.lat,
      lng: hive.lng,
      type: `${hive.name} (${hive.members} üye)`,
      severity: getHiveSeverity(hive.type, hive.status),
      user: `${hive.neighborhood} Mahallesi`,
      timestamp: new Date().toISOString(),
      status: hive.status === 'active' ? 'active' : 'resolved'
    }));

    const getMarkerColor = (status: string, type: string) => {
      if (status === 'active' && type === 'principal') return '#52c41a'; // Green
      if (status === 'active' && type === 'new') return '#1890ff'; // Blue  
      if (status === 'pending') return '#fa8c16'; // Orange
      if (status === 'maintenance') return '#f5222d'; // Red
      if (type === 'guest') return '#722ed1'; // Purple
      return '#d9d9d9'; // Gray default
    };

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <GlobalOutlined />
                <span>🗺️ BeeNetwork Hive Haritası</span>
              </Space>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} size="small">
                  Yenile
                </Button>
                <Button icon={<AimOutlined />} size="small" type="primary">
                  Konumuma Git
                </Button>
              </Space>
            }
          >
            {/* Real Google Maps */}
            <GoogleMap
              alarms={hiveMarkers}
              center={{ lat: 41.0082, lng: 28.9784 }} // Istanbul center
              zoom={11}
              clusterLevel={60}
              onMarkerClick={(hive) => {
                console.log('Hive clicked:', hive);
                // You can add hive detail modal here
              }}
              onAddressResolved={(hive, address) => {
                console.log('Address resolved:', address);
              }}
              onStreetViewOpen={(hive) => {
                console.log('Street View opened for:', hive);
              }}
              onSendMessage={(hive) => {
                console.log('Send message to:', hive);
                // You can integrate with messaging system
              }}
            />

            {/* Map Controls */}
            <div style={{ marginTop: '16px' }}>
              <Row gutter={[8, 8]}>
                <Col>
                  <Tag color="green">
                    <TeamOutlined /> Aktif Principal ({hiveLocations.filter(h => h.status === 'active' && h.type === 'principal').length})
                  </Tag>
                </Col>
                <Col>
                  <Tag color="blue">
                    <UserOutlined /> Yeni Hive ({hiveLocations.filter(h => h.type === 'new').length})
                  </Tag>
                </Col>
                <Col>
                  <Tag color="purple">
                    <UserOutlined /> Misafir ({hiveLocations.filter(h => h.type === 'guest').length})
                  </Tag>
                </Col>
                <Col>
                  <Tag color="orange">
                    <BellOutlined /> Beklemede ({hiveLocations.filter(h => h.status === 'pending').length})
                  </Tag>
                </Col>
                <Col>
                  <Tag color="red">
                    <SettingOutlined /> Bakım ({hiveLocations.filter(h => h.status === 'maintenance').length})
                  </Tag>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Map Statistics */}
            <Card title="📍 Konum İstatistikleri">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic 
                    title="Toplam Hive" 
                    value={hiveLocations.length} 
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Aktif Üye" 
                    value={hiveLocations.reduce((sum, hive) => sum + hive.members, 0)} 
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Kapsanan Mahalle" 
                    value={new Set(hiveLocations.map(h => h.neighborhood)).size} 
                    prefix={<EnvironmentOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Ortalama Üye/Hive" 
                    value={Math.round(hiveLocations.reduce((sum, hive) => sum + hive.members, 0) / hiveLocations.length)} 
                    prefix={<BarChartOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            {/* Hive List */}
            <Card title="📋 Hive Listesi" size="small">
              <List
                size="small"
                dataSource={hiveLocations}
                renderItem={(hive) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          size="small"
                          style={{ backgroundColor: getMarkerColor(hive.status, hive.type) }}
                          icon={<TeamOutlined />}
                        />
                      }
                      title={
                        <Space>
                          <Text strong style={{ fontSize: '12px' }}>{hive.name}</Text>
                          <Badge count={hive.members} size="small" />
                        </Space>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {hive.neighborhood} • {hive.status} • {hive.type}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>

            {/* Quick Actions */}
            <Card title="⚡ Hızlı İşlemler" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button block icon={<PlusOutlined />} type="dashed">
                  Yeni Hive Ekle
                </Button>
                <Button block icon={<RadarChartOutlined />}>
                  Alan Analizi Yap
                </Button>
                <Button block icon={<AimOutlined />}>
                  Optimum Konum Öner
                </Button>
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <TeamOutlined style={{ color: '#1890ff' }} />
            🐝 BeeNetwork Dijital Mesh Ağı
          </Space>
        </Title>
        <Paragraph type="secondary">
          AI destekli mahalle bazlı hive yönetimi, üye takibi ve ağ optimizasyonu
        </Paragraph>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Hive"
              value={stats.totalHives}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Üye"
              value={stats.totalMembers}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Hive"
              value={stats.activeHives}
              prefix={<CheckCircleOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="AI Optimize"
              value={stats.aiOptimized}
              prefix={<RobotOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <Space>
              <TeamOutlined />
              Hive Yönetimi
            </Space>
          } 
          key="hives"
        >
          {renderHives()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <RobotOutlined />
              AI Önerileri
            </Space>
          } 
          key="ai-recommendations"
        >
          {renderAIRecommendations()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <BarChartOutlined />
              Ağ Analizi
            </Space>
          } 
          key="network-analytics"
        >
          {renderNetworkAnalytics()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <GlobalOutlined />
              Hive Haritası
            </Space>
          } 
          key="hive-map"
        >
          {renderHiveMap()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <SettingOutlined />
              Ayarlar
            </Space>
          } 
          key="settings"
        >
          <Card title="BeeNetwork Ağ Ayarları">
            <Form layout="vertical">
              <Form.Item label="Otomatik Hive Oluşturma">
                <Switch defaultChecked /> AI tarafından otomatik hive oluşturma
              </Form.Item>
              <Form.Item label="Üye Dağılım Optimizasyonu">
                <Switch defaultChecked /> Otomatik üye dağılım dengesi
              </Form.Item>
              <Form.Item label="Hive Boyut Limiti">
                <Slider defaultValue={300} min={100} max={500} marks={{ 100: '100', 300: '300', 500: '500' }} />
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>

      {/* Hive Detail Modal */}
      <Modal
        title={`🐝 Hive Detayları: ${selectedHive?.name}`}
        open={hiveDetailModal}
        onCancel={() => setHiveDetailModal(false)}
        footer={[
          <Button key="edit" type="primary" icon={<EditOutlined />}>
            Düzenle
          </Button>,
          <Button key="close" onClick={() => setHiveDetailModal(false)}>
            Kapat
          </Button>
        ]}
        width={800}
      >
        {selectedHive && (
          <div>
            <Tabs defaultActiveKey="overview">
              <TabPane tab="Genel Bakış" key="overview">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Hive ID">{selectedHive.id}</Descriptions.Item>
                  <Descriptions.Item label="Durum">
                    <Tag color={getStatusColor(selectedHive.status)}>
                      {getStatusText(selectedHive.status)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="İlçe">{selectedHive.district}</Descriptions.Item>
                  <Descriptions.Item label="Mahalle">{selectedHive.neighborhood}</Descriptions.Item>
                  <Descriptions.Item label="Oluşturulma">{selectedHive.createdAt}</Descriptions.Item>
                  <Descriptions.Item label="AI Üretimi">
                    {selectedHive.aiGenerated ? 'Evet' : 'Hayır'}
                  </Descriptions.Item>
                </Descriptions>
              </TabPane>
              
              <TabPane tab="Üye Bilgileri" key="members">
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic title="Asil Üye" value={selectedHive.members.asil} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Yeni Üye" value={selectedHive.members.yeni} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Misafir Üye" value={selectedHive.members.misafir} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Aktif Üye" value={selectedHive.members.active} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Doğrulanmış" value={selectedHive.members.verified} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Toplam" value={selectedHive.members.total} />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Performans" key="performance">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Text>Yanıt Süresi: {selectedHive.performance.responseTime} dakika</Text>
                    <Progress 
                      percent={Math.max(0, 100 - (selectedHive.performance.responseTime * 10))} 
                      strokeColor="#52c41a" 
                    />
                  </div>
                  <div>
                    <Text>Topluluk Skoru: {selectedHive.performance.communityScore}</Text>
                    <Progress 
                      percent={selectedHive.performance.communityScore} 
                      strokeColor="#1890ff" 
                    />
                  </div>
                  <div>
                    <Text>Güvenlik İndeksi: {selectedHive.performance.safetyIndex}</Text>
                    <Progress 
                      percent={selectedHive.performance.safetyIndex} 
                      strokeColor="#fa8c16" 
                    />
                  </div>
                </Space>
              </TabPane>

              <TabPane tab="AI Metrikleri" key="ai-metrics">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small" title="Optimal Boyut">
                      <Text style={{ color: selectedHive.aiMetrics.optimalSize ? '#52c41a' : '#ff4d4f' }}>
                        {selectedHive.aiMetrics.optimalSize ? 'Evet' : 'Hayır'}
                      </Text>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Yoğunluk Skoru">
                      <Progress percent={selectedHive.aiMetrics.densityScore} size="small" />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Bağlantı İndeksi">
                      <Progress percent={selectedHive.aiMetrics.connectivityIndex} size="small" />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Verimlilik Oranı">
                      <Progress percent={selectedHive.aiMetrics.efficiencyRating} size="small" />
                    </Card>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Create Hive Modal */}
      <Modal
        title="🐝 Yeni Hive Oluştur"
        open={createHiveModal}
        onCancel={() => setCreateHiveModal(false)}
        footer={null}
        width={600}
      >
        <Alert
          message="AI Destekli Hive Oluşturma"
          description="AI, optimal hive boyutu ve konumunu belirleyerek mahalle bazlı dijital mesh ağı oluşturacak."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        
        <Form
          form={form}
          layout="vertical"
          onFinish={createNewHive}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="İlçe"
                name="district"
                rules={[{ required: true, message: 'İlçe seçin' }]}
              >
                <Select placeholder="İlçe seçin">
                  <Option value="Kadıköy">Kadıköy</Option>
                  <Option value="Şişli">Şişli</Option>
                  <Option value="Beşiktaş">Beşiktaş</Option>
                  <Option value="Üsküdar">Üsküdar</Option>
                  <Option value="Bakırköy">Bakırköy</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Mahalle"
                name="neighborhood"
                rules={[{ required: true, message: 'Mahalle adı girin' }]}
              >
                <Input placeholder="Mahalle adı" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Tahmini Nüfus"
            name="population"
            initialValue={2000}
          >
            <Slider
              min={500}
              max={10000}
              marks={{ 500: '500', 2000: '2K', 5000: '5K', 10000: '10K' }}
            />
          </Form.Item>

          <Form.Item
            label="Kapsama Yarıçapı (km)"
            name="radius"
            initialValue={1.0}
          >
            <Slider
              min={0.5}
              max={3.0}
              step={0.1}
              marks={{ 0.5: '0.5km', 1.0: '1km', 2.0: '2km', 3.0: '3km' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<RobotOutlined />}
              >
                AI ile Hive Oluştur
              </Button>
              <Button onClick={() => setCreateHiveModal(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BeeNetworkPage;